import { Avatar, Col, Popconfirm, Row } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { CiCircleRemove } from "react-icons/ci";
import moment from "moment";
import React, { useEffect, useState } from "react";
import "./index.css";
import { Colors, Fonts, Images } from "../../config";
import {
  Button,
  Modal,
  Pagination,
  Table,
  Text,
  TextField,
} from "../../components";

import { MontserratBold, MontserratRegular } from "../../config/fonts";
import { InterviewsAction } from "../../store/actions";
import {
  FaEye,
  FaFileVideo,
  FaPlay,
  FaDownload,
  FaArrowLeft,
} from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { FiUploadCloud } from "react-icons/fi";

function InterviewManagement() {
  const dispatch = useDispatch();
  const interviews = useSelector((state) => state.interviews);

  // --- States ---
  const [activeTab, setActiveTab] = useState("interviews");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [newBackground, setNewBackground] = useState({
    name: "",
    file: null,
    preview: "",
    type: "user",
  });
  const [state, setState] = useState({
    limit: 10,
    offset: 0,
    keyword: "",
    type: "all",
    page: 1,
  });

  const BUCKET = process.env.REACT_APP_MEDIA_BASE_URL;

  // --- Media/Recordings State ---
  const [mediaPlayerOpen, setMediaPlayerOpen] = useState(false);
  const [recordingList, setRecordingList] = useState([]);
  const [currentPlayingUrl, setCurrentPlayingUrl] = useState("");

  // --- Effects ---
  useEffect(() => {
    if (activeTab === "interviews") {
      dispatch(InterviewsAction.getInterviewsListing(state));
    } else {
      dispatch(InterviewsAction.getBackgroundListing(state));
    }
  }, [state.page, state.type, activeTab, dispatch, state]);

  const fileInputRef = React.useRef(null);

  // --- Handlers ---
  const handlePagination = (pageNumber) => {
    let offset = (pageNumber - 1) * state.limit;
    setState({ ...state, offset: offset, page: pageNumber });
  };

  const handleDeleteInterviews = (id) => {
    dispatch(
      InterviewsAction.deleteInterviews(id, () => {
        dispatch(InterviewsAction.getInterviewsListing(state));
      }),
    );
  };

  const handleOpenRecordings = (calls) => {
    // Filter calls that have a recording
    const validCalls = calls?.filter((c) => c.recording) || [];

    if (validCalls.length === 0) {
      // If you want to show feedback when empty, do it here.
      // For now we just open empty or don't open.
      // Assuming we only want to show if data exists:
      // return alert("No recordings available");
    }

    setRecordingList(validCalls);
    setCurrentPlayingUrl(""); // Ensure we start in grid view
    setMediaPlayerOpen(true);
  };

  const handleDownloadRecording = (e, path) => {
    e.stopPropagation(); // prevent triggering play if nested
    if (!path) return;
    const url = path.startsWith("http") ? path : `${BUCKET}${path}`;
    window.open(url, "_blank");
  };

  const handleViewPreview = (record) => {
    setPreviewData(record);
    setIsPreviewOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewBackground({
        ...newBackground,
        file: file,
        preview: URL.createObjectURL(file),
      });
    }
  };

  const handleCancel = () => {
    setAddModalVisible(false);
    setNewBackground({ name: "", file: null, preview: "", type: "user" });
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleAddBackground = () => {
    if (!newBackground.name || !newBackground.file) {
      return alert("Please enter a name and select an image");
    }
    // Dispatch the action
    dispatch(
      InterviewsAction.addBackground(
        {
          name: newBackground.name,
          image: newBackground.file,
          type: newBackground.type,
        },
        () => {
          handleCancel();
          dispatch(InterviewsAction.getBackgroundListing(state));
        },
      ),
    );
  };

  const handleDeleteBackground = (id) => {
    dispatch(
      InterviewsAction.deleteBackground(id, () => {
        dispatch(InterviewsAction.getBackgroundListing(state));
      }),
    );
  };

  return (
    <>
      <div className="wrapper">
        {/* Title Row */}
        <Row className="flex-row-space-center mb-5 mx-15">
          <Col span={24}>
            <Text.Basic
              text="Interview Management"
              color={Colors.Black}
              fontSize={22}
              fontWeight={600}
              fontFamily={Fonts[MontserratBold]}
              textAlign="left"
            />
          </Col>
        </Row>

        {/* Tabs and Actions Row */}
        <Row className="flex-row-space-center mb-5 mx-15 items-center">
          <Col span={12} className="flex-row items-center">
            {/* First Button: Interviews */}
            <Button.Basic
              size={"large"}
              buttonStyle={{
                backgroundColor:
                  activeTab === "interviews"
                    ? Colors.BlueText
                    : Colors.LightBlue,
                borderRadius: "5px 0px 0px 5px", // Rounded left corners only
                color: activeTab === "interviews" ? Colors.White : Colors.Black,
                padding: "0 30px",
              }}
              onClick={() => {
                setActiveTab("interviews");
                setState((state) => ({ ...state, page: 1, type: "all" }));
              }}
              text={"Interviews"}
            />

            {/* Second Button: Backgrounds */}
            <Button.Basic
              size={"large"}
              buttonStyle={{
                backgroundColor:
                  activeTab === "backgrounds" ? "#7C3AED" : Colors.LightBlue,
                borderRadius: "0px 5px 5px 0px", // Rounded right corners only
                color:
                  activeTab === "backgrounds" ? Colors.White : Colors.Black,
                padding: "0 30px",
              }}
              onClick={() => {
                setActiveTab("backgrounds");
                setState((state) => ({ ...state, page: 1, type: "user" }));
              }}
              text={"Backgrounds"}
            />
          </Col>

          <Col span={12} className="flex justify-end">
            {activeTab === "interviews" ? (
              <div style={{ width: "250px" }}>
                <TextField.SelectField
                  className="listing-filter w-100"
                  size={"large"}
                  allowClear={false}
                  onChange={(value) =>
                    setState({ ...state, page: 1, type: value })
                  }
                  value={state.type || "all"}
                  options={[
                    { label: "Upcoming", value: "all" },
                    { label: "No Show", value: "noShow" },
                    { label: "In Progress", value: "upcoming" },
                    { label: "Completed", value: "completed" },
                  ]}
                />
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div style={{ width: "250px" }}>
                  <TextField.SelectField
                    className="listing-filter w-100"
                    size={"large"}
                    allowClear={false}
                    onChange={(value) =>
                      setState({ ...state, page: 1, type: value })
                    }
                    value={state.type || "user"}
                    options={[
                      // { label: "All Backgrounds", value: "all" },
                      { label: "User Backgrounds", value: "user" },
                      {
                        label: "Artist Backgrounds",
                        value: "artist",
                      },
                    ]}
                  />
                </div>
                <button
                  onClick={() => setAddModalVisible(true)}
                  className="bg-[#7C3AED] text-white px-5 py-1.5 rounded-md flex items-center gap-2 hover:bg-purple-700 transition-colors font-medium"
                >
                  <span className="text-xl">+</span> Add Background
                </button>
              </div>
            )}
          </Col>
        </Row>

        {/* Table Rendering */}
        {activeTab === "interviews" ? (
          <Table.Basic
            columns={[
              {
                title: "Sno",
                dataIndex: "sno",
                key: "sno",
                render: (text, record, i) => (
                  <span className="ml-10 table-cell">
                    {state.offset + (i + 1)}
                  </span>
                ),
              },
              {
                title: "User Name",
                dataIndex: "candidate",
                key: "candidate",
                render: (text, record) => (
                  <span className="ml-10 table-cell">
                    {record.candidate?.name || "N/A"}
                  </span>
                ),
              },
              {
                title: "Artist Name",
                dataIndex: "artist",
                key: "artist",
                render: (text, record) => (
                  <span className="ml-10 table-cell">
                    {record.slot?.artist?.name || "N/A"}
                  </span>
                ),
              },
              {
                title: "Payment Date",
                dataIndex: "date",
                key: "date",
                render: (text, record) => (
                  <span className="ml-10 table-cell">
                    {moment(record?.chat?.created_at).format("DD-MM-YYYY")}
                  </span>
                ),
              },
              {
                title: "File",
                dataIndex: "file",
                key: "file",
                render: (text, record) => {
                  const hasRecordings = record?.chat?.calls?.some(
                    (c) => c.recording,
                  );

                  if (!hasRecordings) {
                    return <span className="table-cell">N/A</span>;
                  }

                  return (
                    <Avatar
                      size={50}
                      shape="square"
                      icon={<FaFileVideo />}
                      className="cursor-pointer"
                      onClick={() => handleOpenRecordings(record?.chat?.calls)}
                    />
                  );
                },
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (text, record) => (
                  <div className="flex-row-align-center gap-0">
                    <span className="table-cell mx-15 custom-style text-[#F0AC02] background-color-[#f0ac021a]">
                      {record?.status}
                    </span>
                    <Popconfirm
                      title="Delete record"
                      onConfirm={() => handleDeleteInterviews(record?.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <img
                        src={Images.DeleteIcon}
                        width={28}
                        className="cursor"
                        alt="delete"
                      />
                    </Popconfirm>
                  </div>
                ),
              },
            ]}
            data={interviews?.interviews || []}
            loading={interviews.isLoading}
          />
        ) : (
          <Table.Basic
            columns={[
              {
                title: "Image",
                dataIndex: "image_path",
                key: "image_path",
                align: "left",
                width: 100,
                render: (url) => (
                  <img
                    src={`${BUCKET}${url}`}
                    alt="bg"
                    style={{
                      width: 45,
                      height: 45,
                      borderRadius: 6,
                      objectFit: "cover",
                    }}
                  />
                ),
              },
              {
                title: "Name",
                dataIndex: "name",
                key: "name",
                align: "left",
                render: (name) => (
                  <span className="ml-10 table-cell font-semibold text-gray-700">
                    {name}
                  </span>
                ),
              },
              {
                title: "Actions",
                key: "actions",
                width: 150,
                render: (_, record) => (
                  <div className="flex items-center gap-3">
                    <FaEye
                      className="cursor-pointer"
                      color={"#afc8e9"}
                      size={24}
                      onClick={() => handleViewPreview(record)}
                    />

                    <Popconfirm
                      title="Delete this background?"
                      onConfirm={() => handleDeleteBackground(record?.id)}
                    >
                      <MdDelete
                        size={24}
                        color="#FF606B"
                        className="cursor-pointer"
                      />
                    </Popconfirm>
                  </div>
                ),
              },
            ]}
            data={interviews?.backgrounds}
            loading={interviews.loadingBackgrounds}
          />
        )}

        {/* Pagination - Visible only for Interviews as per your logic */}
        {activeTab === "interviews" && interviews.count > 10 && (
          <Row justify={"center"} className="mt-10">
            <Col>
              <Pagination.Basic
                onChange={handlePagination}
                pageSize={10}
                current={state.page}
                total={interviews.count}
              />
            </Col>
          </Row>
        )}
        {activeTab !== "interviews" && interviews.total > 10 && (
          <Row justify={"center"} className="mt-10">
            <Col>
              <Pagination.Basic
                onChange={handlePagination}
                pageSize={10}
                current={state.page}
                total={interviews.total}
              />
            </Col>
          </Row>
        )}
      </div>

      {/* Preview Modal */}
      <Modal.Basic
        centered={true}
        visible={isPreviewOpen}
        onCancel={() => setIsPreviewOpen(false)}
        footer={null}
        closable={true}
        className="modal-lg"
      >
        <div className="text-center font-bold text-lg mb-2">
          {previewData?.name || "Background Preview"}
        </div>
        <div className="flex justify-center items-center">
          {previewData?.imageUrl || previewData?.image_path ? (
            <img
              src={
                previewData?.imageUrl
                  ? previewData.imageUrl
                  : `${BUCKET}${previewData.image_path}`
              }
              alt="Preview"
              className="w-full max-h-[65vh] object-cover rounded-xl"
            />
          ) : (
            <Text.Basic text="No Image Available" />
          )}
        </div>
      </Modal.Basic>
      {/* Add Background Modal */}
      <Modal.Basic
        centered={true}
        open={addModalVisible}
        handleCancel={handleCancel}
        footer={null}
        closable={true}
        className="modal-lg"
      >
        <div className="px-5 py-2">
          <div className="text-center font-bold text-xl">Add Background</div>
          {/* Name and Type Input */}
          <div className="flex flex-col gap-4 my-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Name:
              </label>
              <TextField.Basic
                value={newBackground.name}
                onChange={(e) =>
                  setNewBackground({ ...newBackground, name: e.target.value })
                }
                placeholder="Enter background name"
                className="w-full"
              />
            </div>
            <div className="mb-4 flex-1">
              <label className="block text-gray-700 font-semibold mb-2">
                Type:
              </label>
              <TextField.SelectField
                className="listing-filter w-100"
                size={"large"}
                allowClear={false}
                inputStyle={{
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  padding: "2px",
                }}
                onChange={(value) =>
                  setNewBackground({ ...newBackground, type: value })
                }
                value={newBackground.type}
                options={[
                  { label: "User", value: "user" },
                  { label: "Artist", value: "artist" },
                ]}
              />
            </div>
          </div>

          {/* Upload Area / Preview */}
          <div
            className="relative border-2 border-dashed border-purple-300 rounded-xl p-2 flex flex-col items-center justify-center bg-purple-50 cursor-pointer hover:bg-purple-100 transition-all"
            style={{ minHeight: "180px" }}
            onClick={() => document.getElementById("bg-upload").click()}
          >
            <input
              id="bg-upload"
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
            />

            {newBackground.preview ? (
              <img
                src={newBackground.preview}
                alt="Preview"
                className="w-full h-40 object-cover rounded-lg"
              />
            ) : (
              <div className="flex flex-col items-center justify-center">
                <FiUploadCloud size={40} color={Colors.BlueText} />
                <h2
                  className="text-xl font-semibold"
                  style={{ color: Colors.BlueText }}
                >
                  Upload a Background
                </h2>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button.Basic
            size={"large"}
            className="w-full mt-6"
            buttonStyle={{
              backgroundColor: "#7C3AED",
              color: Colors.White,
              borderRadius: "10px",
              height: "50px",
              fontWeight: "bold",
            }}
            text={interviews.isLoading ? "Uploading..." : "Submit"}
            onClick={handleAddBackground}
          />
        </div>
      </Modal.Basic>

      <Modal.Basic
        className="modal-lg"
        centered={true}
        title={null}
        destroyOnClose={true}
        open={mediaPlayerOpen}
        handleCancel={() => {
          setMediaPlayerOpen(false);
          setRecordingList([]);
          setCurrentPlayingUrl("");
        }}
        footer={null}
      >
        <div className="p-4">
          <div className="flex items-center justify-between">
            {currentPlayingUrl ? (
              <button
                onClick={() => setCurrentPlayingUrl("")}
                className="flex items-center gap-2  font-semibold self-start"
                style={{ color: Colors.BlueText }}
                title="Back"
              >
                <FaArrowLeft size={20} />
              </button>
            ) : (
              <div></div>
            )}

            <h3 className="text-xl font-bold mb-4 text-center">
              {currentPlayingUrl ? "Playback" : "Recordings"}
            </h3>
            <div></div>
          </div>

          {currentPlayingUrl ? (
            <div className="flex flex-col gap-2">
              {/* <button
                onClick={() => setCurrentPlayingUrl("")}
                className="flex items-center gap-2  font-semibold self-start"
                style={{ color: Colors.BlueText }}
              >
                <FaArrowLeft size={20} />
              </button> */}
              <div className="w-full aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  src={currentPlayingUrl}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto p-2">
              {recordingList && recordingList.length > 0 ? (
                recordingList.map((call, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center p-4 border rounded-xl shadow-sm hover:shadow-md transition bg-white gap-3"
                  >
                    {/* <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">
                      <FaFileVideo size={30} />
                    </div> */}

                    <Avatar
                      size={50}
                      shape="square"
                      icon={<FaFileVideo />}
                      className="cursor-pointer"
                    />
                    <div className="text-sm font-medium text-gray-500">
                      {call.slot_date
                        ? moment(call.slot_date).format("DD MMM YYYY")
                        : "Unknown Date"}
                    </div>

                    <div className="flex items-center justify-center gap-2 w-full mt-2">
                      <button
                        onClick={() => {
                          const path = call.recording;
                          const url = path?.startsWith("http")
                            ? path
                            : `${BUCKET}${path}`;
                          setCurrentPlayingUrl(url);
                        }}
                        className="p-4 text-white rounded-full text-sm transition"
                        style={{ backgroundColor: Colors.BlueText }}
                        title="Play"
                      >
                        <FaPlay size={12} />
                      </button>
                      <button
                        onClick={(e) =>
                          handleDownloadRecording(e, call.recording)
                        }
                        className="p-4 text-white rounded-full text-sm transition"
                        style={{ backgroundColor: Colors.LightGray }}
                        title="Download"
                      >
                        <FaDownload size={12} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center text-gray-500 py-10">
                  No recordings found.
                </div>
              )}
            </div>
          )}
        </div>
      </Modal.Basic>
    </>
  );
}

export default InterviewManagement;
